import { left, right, Either } from "fp-ts/lib/Either";
import { CSSSelector, CSSSelectorListV } from "@/types/selectors";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { _Error, logErrorsToUiStore } from "@/lib/wordpress/data";
import { globalStore } from "@/store/store-accessor";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import * as t from "io-ts";
import { isUiPanelOpen } from "@/lib/panels/panels";
import { breakdanceUndo } from "@/lib/undo/undo";
import NotificationLogger from "@/lib/notifications/notifications";

/** https://stackoverflow.com/questions/448981/which-characters-are-valid-in-css-class-names-selectors */
export function isValidClassName(name: string) {
  return /^-?[.]+[_a-zA-Z]+[_a-zA-Z0-9-]*$/.test(name);
}

/** https://stackoverflow.com/questions/34849001/check-if-css-selector-is-valid */
export function isValidSelectorName(name: string) {
  try {
    document.createDocumentFragment().querySelector(name);
  } catch {
    return false;
  }
  return true;
}

export function addSelector(newSelector: CSSSelector) {
  /* these errors should never appear - a check should happen before this point */
  if (newSelector.type === "class") {
    if (!isValidClassName(newSelector.name)) {
      NotificationLogger.log.message(
        `${newSelector.name} is not a valid CSS class name.`
      );
      return;
    }
  } else {
    if (!isValidSelectorName(newSelector.name)) {
      NotificationLogger.log.message(
        `${newSelector.name} is not a valid CSS selector.`
      );

      return;
    }
  }

  if (
    globalStore.cssSelectors.find(
      selector => selector.name === newSelector.name
    )
  ) {
    NotificationLogger.log.message(`${newSelector.name} is already present.`);
    return;
  }

  breakdanceUndo.transaction(
    () => globalStore.setSelectors([...globalStore.cssSelectors, newSelector]),
    "Add Selector"
  );
}

export function addSelectorToStoreAndSaveSelectorsToWordPressIfSelectorsPanelIsClosed(
  selector: CSSSelector
) {
  addSelector(selector);

  // TODO not sure whether this check should exist here, it's inspired by SaveColorDialog.saveAsGlobal()'s logic
  if (!isUiPanelOpen("selectors")) {
    saveSelectorsToWordPress();
  }
}

export function createCssSelectorObjectFromSelectorName(
  selectorName: string
): Either<string[], CSSSelector> {
  if (!isValidSelectorName(selectorName)) {
    return left([`"${selectorName}" is not a valid CSS selector.`]);
  }

  const isClass = !!isValidClassName(selectorName);

  return right({
    name: selectorName,
    type: isClass ? "class" : "custom",
    properties: {}
  });
}

export async function refetchSelectorsFromServerToGlobalStore(): Promise<boolean> {
  const selectorsFromServerOrErrors = await makeWordPressAjaxRequest({
    action: "breakdance_load_selectors",
    decoder: CSSSelectorListV
  });

  return pipe(
    selectorsFromServerOrErrors,
    fold(
      errors => {
        logErrorsToUiStore(
          errors.map(err => {
            return {
              ...err,
              customNotificationToShow: err.customNotificationToShow
                ? err.customNotificationToShow.type === "INTERNAL_SERVER_ERROR"
                  ? { ...err.customNotificationToShow, keepOpen: true }
                  : err.customNotificationToShow
                : undefined
            };
          })
        );
        return false;
      },
      loadedData => {
        breakdanceUndo.ignore(() => {
          globalStore.setSelectors(loadedData);
          globalStore.markCurrentSelectorsAsUntouched();
        });
        return true;
      }
    )
  );
}

export async function saveSelectorsToWordPress(): Promise<boolean> {
  return pipe(
    await makeWordPressAjaxRequest({
      friendlyName: "Saving selectors to WordPress",
      action: "breakdance_save_selectors",
      dataToPost: {
        selectors: fpJsonStringify(globalStore.cssSelectors)
      },
      decoder: t.unknown
    }),
    fold(
      (errors: _Error[]) => {
        logErrorsToUiStore(errors);
        return false;
      },
      () => {
        breakdanceUndo.ignore(() =>
          globalStore.markCurrentSelectorsAsUntouched()
        );
        return true;
      }
    )
  );
}
